import React from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import { swSettings } from "app/common/services/swSettings";
import IntegrationButtonWrapper from "app/pages/sales-intelligence/modules/manage-integrations/components/integration-button/IntegrationButtonWrapper";
import manageIntegrationsTrackingService from "app/pages/sales-intelligence/modules/manage-integrations/services/tracking";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { isAfterTrial, isNoTouch, isTrial } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import { SignalsRestApiCard } from "../../components/integration-cards/SignalsRestApiCard";
import { API_ITEMS } from "../../constants/api-items";
import useSignalsRestApiData from "../../hooks/useSignalsRestApiData";
import {
    MANAGE_INTEGRATIONS_APIS_TITLE,
    MANAGE_INTEGRATIONS_CARD_ACTIVATE,
    MANAGE_INTEGRATIONS_CARD_UPGRADE,
} from "../../constants/translations";
import type { ApiPageType, IAPIItem } from "../../types/api-page-type";
import openExternalLink from "../../utils/openExternalLink";
import ManagementCardFilterTag from "../cards-components/ManagementCardFilterTag";
import ManagementSmallCard from "../cards-components/ManagementSmallCard";
import {
    StyledManagementCardFooterWrapper,
    StyledManagementCardIconButton,
    StyledManagementCardTagWrapper,
} from "../cards-components/styles";
import CardsWrapper from "../CardsWrapper/CardsWrapper";

const APIsCardsList = (): React.ReactElement => {
    const { translate } = useTranslate();
    const signalsRestApiData = useSignalsRestApiData();

    const isEnabled = !isTrial(swSettings) && !isAfterTrial(swSettings) && !isNoTouch(swSettings);

    const handleNavigateToDescriptionPage = (apiName?: ApiPageType) => {
        if (!apiName) {
            return;
        }

        manageIntegrationsTrackingService.trackApiCardClick(apiName);
        void siNavigator.goToGatewayPage(apiName);
    };

    const handleHeaderActionClick = (item: IAPIItem) => {
        if (item.name) {
            manageIntegrationsTrackingService.trackApiGuideClick(item.name);
        }

        if (item.tutorialLink) {
            openExternalLink(item.tutorialLink);
        }
    };

    const handleUpgradeClick = (apiType: ApiPageType) => {
        manageIntegrationsTrackingService.trackApiUpgradeClick(apiType);
    };

    return (
        <CardsWrapper title={translate(MANAGE_INTEGRATIONS_APIS_TITLE)}>
            <SignalsRestApiCard
                key="signals-rest-api"
                {...signalsRestApiData}
            />
            {API_ITEMS.map((item) => (
                <React.Fragment key={item.titleKey}>
                    <ManagementSmallCard
                        isStaticHeight
                        key={item.titleKey}
                        dataAutomation={item.dataAutomation}
                        title={translate(item.titleKey)}
                        message={translate(item.descriptionKey)}
                        onHeaderActionClick={item.tutorialLink ? () => handleHeaderActionClick(item) : undefined}
                    >
                        <StyledManagementCardFooterWrapper>
                            <StyledManagementCardTagWrapper>
                                {item.price ? (
                                    <ManagementCardFilterTag iconName="spend">
                                        {translate(item.price.key, item.price?.replacements)}
                                    </ManagementCardFilterTag>
                                ) : null}

                                {item.labels?.map((label) => (
                                    <ManagementCardFilterTag
                                        key={label}
                                        iconName="spend"
                                    >
                                        {translate(label)}
                                    </ManagementCardFilterTag>
                                ))}
                            </StyledManagementCardTagWrapper>

                            <IntegrationButtonWrapper
                                isEnabled={isEnabled}
                                dataAutomationNameSpace="api-card"
                                upgradeButtonText={translate(MANAGE_INTEGRATIONS_CARD_UPGRADE)}
                                onUpgradeClick={() => handleNavigateToDescriptionPage(item.name)}
                            >
                                <StyledManagementCardIconButton
                                    iconName="link-out"
                                    placement="right"
                                    isDisabled={!item.name}
                                    onClick={() => handleNavigateToDescriptionPage(item.name)}
                                >
                                    {translate(MANAGE_INTEGRATIONS_CARD_ACTIVATE)}
                                </StyledManagementCardIconButton>
                            </IntegrationButtonWrapper>
                        </StyledManagementCardFooterWrapper>
                    </ManagementSmallCard>
                </React.Fragment>
            ))}
        </CardsWrapper>
    );
};

export default APIsCardsList;
